import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Code, Plus, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { listWork4MeScripts } from '@/api/scripts-api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Work4MeScriptCreate } from './Work4MeScriptCreate';

export function Work4MeScriptList() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [showNewScriptDialog, setShowNewScriptDialog] = useState(false);
  const scriptsQuery = useQuery({
    queryKey: ['work4me-scripts'],
    queryFn: listWork4MeScripts,
  });
  const filteredScripts = useMemo(() => {
    const scripts = scriptsQuery.data?.items ?? [];
    const term = search.trim().toLowerCase();
    if (!term) {
      return scripts;
    }
    return scripts.filter((script) =>
      script.name.toLowerCase().includes(term)
      || (script.description ?? '').toLowerCase().includes(term)
      || script.id.toString().includes(term)
    );
  }, [search, scriptsQuery.data]);

  return (
    <div className="-mx-4 py-2">
      <div className="px-4 py-3 border-b bg-muted/20">
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-xl font-light tracking-wide">Scripts</h2>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <div className="relative w-[240px] sm:w-[280px]">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search scripts..."
                  className="h-9 pl-10"
                />
              </div>
              <Button onClick={() => setShowNewScriptDialog(true)} className="gap-2 h-9 px-3">
                <Plus className="h-4 w-4" />
                Script
              </Button>
            </div>
          </div>
          <p className="text-muted-foreground">
            Deterministic calculations (sandboxed JavaScript, JSON in and out) that you, Neo and agents can run.
          </p>
        </div>
      </div>

      {scriptsQuery.isLoading ? (
        <div className="px-4 py-6 text-sm text-muted-foreground">Loading scripts...</div>
      ) : null}
      {scriptsQuery.isError ? (
        <div className="px-4 py-6 text-sm text-destructive">Unable to load scripts.</div>
      ) : null}
      {!scriptsQuery.isLoading && !scriptsQuery.isError && filteredScripts.length === 0 ? (
        <div className="px-4 py-6 text-sm text-muted-foreground">
          {search.trim() ? 'No scripts match your search.' : 'Nothing here yet — scripts you create appear here.'}
        </div>
      ) : null}

      {!scriptsQuery.isLoading && !scriptsQuery.isError && filteredScripts.length > 0 ? (
        <ul className="divide-y">
          {filteredScripts.map((script) => (
            <li
              key={script.id}
              data-testid="work4me-script-entry"
              className="flex items-center gap-2 px-4 py-3 hover:bg-muted/30 transition-colors cursor-pointer"
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/work4me/scripts/${script.id}`)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  navigate(`/work4me/scripts/${script.id}`);
                }
              }}
              aria-label={`Open ${script.name}`}
            >
              <Code className="h-4 w-4 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium truncate">{script.name}</p>
                  <Badge variant="outline">{script.ownerScope}</Badge>
                  <Badge variant={script.isEnabled ? 'default' : 'secondary'}>
                    {script.isEnabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                  {script.hasInputExample ? <Badge variant="outline">example</Badge> : null}
                </div>
                <p className="text-xs text-muted-foreground truncate">{script.description ?? 'No description'}</p>
              </div>
              <Badge variant="secondary" className="text-[10px]">ID {script.id}</Badge>
            </li>
          ))}
        </ul>
      ) : null}

      <Work4MeScriptCreate
        open={showNewScriptDialog}
        onOpenChange={setShowNewScriptDialog}
        onCreated={(scriptId) => navigate(`/work4me/scripts/${scriptId}`)}
      />
    </div>
  );
}
